import { Text, View } from "react-native";

import { cn } from "@/lib/ui/cn";
import { owedLabel } from "@/lib/ui/qty";

/** A small rounded pill holding one short label. */
export function Badge({
  label,
  className,
  textClassName,
  testID,
  "aria-hidden": ariaHidden,
}: {
  label: string;
  className?: string;
  textClassName?: string;
  /** Names the badge for a harness rung; the three surfaces share this one selector. */
  testID?: string;
  /**
   * Hides a badge that only restates what its container already announces. Use `aria-hidden`, not
   * the native-only pair, which react-native-web drops (mobile/AGENTS.md).
   */
  "aria-hidden"?: boolean;
}) {
  return (
    <View
      testID={testID}
      className={cn("self-start rounded-full px-2 py-0.5", className)}
      aria-hidden={ariaHidden}
    >
      {
        /* A badge label is one short token, so it clamps to one line. With a `shrink` class from
          the caller it ellipsizes instead of wrapping the pill to two lines. */
      }
      <Text className={cn("text-[11px] font-label tracking-wide", textClassName)} numberOfLines={1}>
        {label}
      </Text>
    </View>
  );
}

/** A card's condition (NM/LP/MP/HP/DMG). */
export function ConditionChip(
  { condition, className }: { condition: string; className?: string; },
) {
  return (
    <Badge
      label={condition}
      className={cn("bg-surface-muted", className)}
      textClassName="text-muted-foreground"
    />
  );
}

/**
 * The on-hand copies of one unit (`×3`), for `CardTile`'s badge slot at the art's top-right in the
 * inventory stock grid and the public browse grid (#413).
 *
 * It uses a fixed dark ground rather than a theme surface because the art beneath it is arbitrary,
 * as the scan camera overlays do. It is hidden from assistive tech because every tile that shows it
 * already voices the count in its own `accessibilityLabel`.
 */
export function QtyBadge({ qty }: { qty: number; }) {
  return <Badge label={`×${qty}`} className="bg-black/70" textClassName="text-white" aria-hidden />;
}

/**
 * A unit at negative qty, which is oversell debt.
 *
 * A Channel double-sale can sell the same copy twice before the delist propagates (TRO-224,
 * TRO-241), and negative qty is deliberate rather than an error to clamp away (TRO-233). It fills
 * the same slots as `QtyBadge`, including over arbitrary card art, so it uses the same fixed dark
 * scrim; a `loss` theme token would flip to the wrong side of a photograph (DESIGN.md section 6).
 * The state is carried by the shared `owedLabel` wording, never by color. It is hidden from
 * assistive tech because both call sites already fold `owedLabel(qty).a11y` into their own
 * `accessibilityLabel`.
 */
export function OwedBadge({ qty }: { qty: number; }) {
  return (
    <Badge
      label={owedLabel(qty).text}
      className="bg-black/70"
      textClassName="text-white"
      aria-hidden
    />
  );
}

/** A strategy recommendation, colored by P&L meaning: list is gain, hold is brand. */
export function ActionChip({ action }: { action: "hold" | "list"; }) {
  const box = { hold: "bg-accent-soft", list: "bg-gain/10" }[action];
  const text = { hold: "text-primary", list: "text-gain" }[action];
  return <Badge label={action.toUpperCase()} className={box} textClassName={text} />;
}
